import React from 'react';
import { X, Eye, EyeOff, RotateCcw, Grid, List } from 'lucide-react';
import type { WidgetPreferences } from '../hooks/useWidgetPreferences';
import { useI18n } from '../contexts/I18nContext';

interface WidgetSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  preferences: WidgetPreferences;
  availableWidgets: Array<{
    id: string;
    title: string;
    description?: string;
  }>;
  onToggleVisibility: (widgetId: string, visible: boolean) => void;
  onSetLayout: (layout: 'grid' | 'list') => void;
  onReset: () => void;
}

export const WidgetSettingsModal: React.FC<WidgetSettingsModalProps> = ({
  isOpen,
  onClose,
  preferences,
  availableWidgets,
  onToggleVisibility,
  onSetLayout,
  onReset,
}) => {
  const { t } = useI18n();

  if (!isOpen) return null;

  const getWidgetConfig = (widgetId: string) => {
    return preferences.widgets.find(w => w.id === widgetId);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: 'clamp(300px, 90%, 600px)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>{t('dashboardConfigureWidgets')}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '5px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#333' }}>{t('dashboardLayout')}</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={() => onSetLayout('grid')}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: preferences.layout === 'grid' ? '#9333ea' : '#f3f4f6',
                color: preferences.layout === 'grid' ? 'white' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontWeight: 600,
              }}
            >
              <Grid size={18} />
              {t('dashboardLayoutGrid')}
            </button>
            <button
              type="button"
              onClick={() => onSetLayout('list')}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: preferences.layout === 'list' ? '#9333ea' : '#f3f4f6',
                color: preferences.layout === 'list' ? 'white' : '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontWeight: 600,
              }}
            >
              <List size={18} />
              {t('dashboardLayoutList')}
            </button>
          </div>
        </div>

        <div style={{ padding: '20px', flexGrow: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>{t('dashboardAvailableWidgets')}</h3>
            <button
              type="button"
              onClick={onReset}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#666',
                fontSize: '14px',
              }}
            >
              <RotateCcw size={14} />
              {t('dashboardRestoreDefault')}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {availableWidgets.map(widget => {
              const config = getWidgetConfig(widget.id);
              const isVisible = config?.visible ?? true;

              return (
                <div
                  key={widget.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: isVisible ? '#f9fafb' : '#f3f4f6',
                    borderRadius: '8px',
                    border: `1px solid ${isVisible ? '#e5e7eb' : '#d1d5db'}`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#333', marginBottom: '4px' }}>{widget.title}</div>
                    {widget.description && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{widget.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleVisibility(widget.id, !isVisible)}
                    style={{
                      backgroundColor: isVisible ? '#10b981' : '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    {isVisible ? <Eye size={16} /> : <EyeOff size={16} />}
                    {isVisible ? 'Visível' : 'Oculto'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              backgroundColor: '#9333ea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};
